import React, { useEffect, useState } from 'react';
import { Check, ChevronsUpDown, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { joApi } from '@/services/processingJoApi';
import { fmtN } from './JoBits';

/** FG Name typeahead (FG code or design) — only FGs with base articles in the Sort No master. */
export const FgPicker = ({ value, onPick, testId = 'jo-fg' }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  const [more, setMore] = useState(false);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!open) return undefined;
    setLoading(true);
    const t = setTimeout(() => joApi.fgSearch(q).then((r) => { setRows(r.fgs || []); setMore(!!r.more); })
      .catch(() => { setRows([]); setMore(false); }).finally(() => setLoading(false)), 250);
    return () => clearTimeout(t);
  }, [q, open]);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" className="w-full justify-between font-normal h-auto min-h-10 py-2" data-testid={`${testId}-trigger`}>
          {value ? <span className="text-left min-w-0"><span className="font-mono block truncate">{value.fg_name}</span>
            <span className="block truncate text-[11px] text-gray-500">{[value.design_name, value.shade, value.size].filter(Boolean).join(' · ')}</span></span>
            : <span className="text-gray-400">Search FG name or design…</span>}
          <ChevronsUpDown className="w-4 h-4 opacity-50 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[--radix-popover-trigger-width] min-w-[300px]" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Type FG name or design…" value={q} onValueChange={setQ} data-testid={`${testId}-search`} />
          <CommandList className="max-h-72">
            {loading && <div className="py-4 flex justify-center"><Loader2 className="w-4 h-4 animate-spin" /></div>}
            {!loading && <CommandEmpty><span data-testid={`${testId}-empty`}>No FG found — an FG shows here once a sort no is linked to it in the Sort No master</span></CommandEmpty>}
            {!loading && rows.map((f) => (
              <CommandItem key={f.fg_name} value={f.fg_name} onSelect={() => { onPick(f); setOpen(false); }} data-testid={`${testId}-option-${f.fg_name}`}>
                <Check className={`w-4 h-4 mr-2 shrink-0 ${value?.fg_name === f.fg_name ? 'opacity-100' : 'opacity-0'}`} />
                <div className="min-w-0">
                  <p className="font-mono text-sm truncate">{f.fg_name} <span className="font-sans text-gray-600">{f.design_name}</span></p>
                  <p className="text-[11px] text-gray-500 truncate">{[f.shade, f.size].filter(Boolean).join(' · ')} · {f.base_articles} base article(s) · {fmtN(f.free_pcs)} pcs free</p>
                </div>
              </CommandItem>
            ))}
            {!loading && more && <p className="px-3 py-2 text-[11px] text-gray-500 border-t" data-testid={`${testId}-more`}>Showing the first {rows.length} matches — type more of the FG code, design or shade to narrow down</p>}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default FgPicker;
